import { memo, useState } from 'react';
import { Badge, StatusMessage } from '@aimsos/ui';
import { useTranslation } from '@aimsos/i18n';
import RequestVerificationModal from './RequestVerificationModal.jsx';
import { ShieldCheck } from 'lucide-react';

const STATUS_BADGE = {
  pending: { variant: 'warning', labelKey: 'attestation.status.pending' },
  verified: { variant: 'success', labelKey: 'attestation.status.verified' },
  rejected: { variant: 'danger', labelKey: 'attestation.status.rejected' },
};

const DOT_CLASS = {
  pending: 'bg-amber-400',
  verified: 'bg-emerald-500',
  rejected: 'bg-red-500',
};

const AttestationCard = memo(function AttestationCard({ entry, statusLabel, requestLabel, onRequest }) {
  const statusMeta = STATUS_BADGE[entry.status] ?? STATUS_BADGE.pending;

  return (
    <div className="rounded-[10px] border border-edge bg-surface px-[18px] py-4">
      <div className="flex items-center gap-2">
        <span
          className={`h-[7px] w-[7px] shrink-0 rounded-full ${DOT_CLASS[entry.status] ?? DOT_CLASS.pending}`}
          aria-hidden="true"
        />
        <div className="min-w-0 flex-1">
          <p className="truncate text-[12.5px] font-semibold text-content">{entry.name}</p>
          <p className="text-[10.5px] text-content-faint">{entry.timestamp}</p>
        </div>
        <Badge variant={statusMeta.variant} size="small" radius="default">
          {statusLabel}
        </Badge>
      </div>

      <button
        type="button"
        onClick={onRequest}
        className="mt-3 w-fit text-left text-xs text-content-muted transition-colors hover:text-content"
      >
        {requestLabel}
      </button>
    </div>
  );
});

/**
 * Attestation — content-sized side card (aims-htl `.wqep-side-block`).
 * “Request verification →” opens the Formal Verification modal.
 */
function AttestationTab({ entries = [], event = null }) {
  const { t } = useTranslation('work-queues-app');
  const [modalOpen, setModalOpen] = useState(false);

  if (entries.length === 0) {
    return (
      <div className="rounded-[10px] border border-edge bg-surface px-4 py-4">
        <StatusMessage
          icon={ShieldCheck}
          title={t('attestation.title')}
          subtitle={t('attestation.subtitle')}
        />
      </div>
    );
  }

  return (
    <>
      <div className="flex flex-col gap-3">
        {entries.map((entry) => {
          const statusMeta = STATUS_BADGE[entry.status] ?? STATUS_BADGE.pending;
          return (
            <AttestationCard
              key={entry.id}
              entry={entry}
              statusLabel={t(statusMeta.labelKey)}
              requestLabel={t('attestation.requestVerification')}
              onRequest={() => setModalOpen(true)}
            />
          );
        })}
      </div>

      <RequestVerificationModal
        key={modalOpen ? `open-${event?.id ?? 'event'}` : 'closed'}
        isOpen={modalOpen}
        event={event}
        onClose={() => setModalOpen(false)}
      />
    </>
  );
}

export default AttestationTab;
